import { Fragment, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Typography,
} from '@mui/material';
import { useNavigate, useLocation, matchPath } from 'react-router';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { DrawerContentConfig, StyleObj } from '../../../../@types';
import { getAccountDrawerConfig } from '../../../../config/accountDrawer';
import useLocalization from '../../../../hooks/useLocalization';
import useProductAvailability from '../../../../hooks/useProductAvailability';
import { useKycVerificationEnabled } from '../../../../hooks/components/useKycVerificationAccountSection';
import { useSkin } from '../../../../contexts/SkinContext';

const styles: StyleObj = {
  list: {
    py: 0,
  },
  myAccountItem: {
    p: 0,
    borderBottom: 1,
    borderColor: 'neutral.700',
  },
  myAccountButton: {
    px: 1.5,
    py: 1.5,
    '& .MuiListItemIcon-root': {
      minWidth: 32,
    },
    '& .MuiSvgIcon-root': {
      color: 'neutral.300',
    },
  },
  myAccountText: {
    color: 'neutral.100',
  },
  accordion: {
    backgroundColor: 'neutral.800',
    '&:hover': {
      backgroundColor: 'neutral.700',
    },
    '&.Mui-expanded': {
      backgroundColor: 'neutral.700',
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionDetails-root': {
      p: 0,
    },
    '&::before': {
      height: 0,
    },
  },
  accordionActive: {
    backgroundColor: 'neutral.700',
    '&:hover': {
      backgroundColor: 'neutral.700',
    },
    '&.Mui-expanded': {
      backgroundColor: 'neutral.700',
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionDetails-root': {
      p: 0,
    },
    '&::before': {
      height: 0,
    },
  },
  accordionSummary: {
    px: 1.5,
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
    },
    '& .MuiSvgIcon-root': {
      color: 'neutral.300',
    },
  },
  expandIcon: {
    color: 'neutral.400',
    mr: -0.5,
  },
  childItem: {
    p: 0,
    borderBottom: 1,
    borderColor: 'neutral.700',
  },
  childButton: {
    px: 2,
    py: 1,
  },
  childDivider: {
    borderColor: 'neutral.700',
    mx: 2,
  },
};

const GolaAccountDrawerContent = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useLocalization();
  const { isCasinoOnlyProduct, isSportsbookOnlyProduct } = useProductAvailability();
  const includeVerification = useKycVerificationEnabled();

  const { skin } = useSkin();
  const config = getAccountDrawerConfig(t, skin, {
    includeVerification,
    isCasinoOnlyProduct,
    isSportsbookOnlyProduct,
  });

  return (
    <List sx={styles.list}>
      <ListItem sx={styles.myAccountItem}>
        <ListItemButton sx={styles.myAccountButton} onClick={() => navigate('/my-account')} disableGutters>
          <ListItemIcon>
            <AccountCircleIcon />
          </ListItemIcon>
          <Typography variant='body2' sx={styles.myAccountText}>
            {t('myAccount')}
          </Typography>
        </ListItemButton>
      </ListItem>

      {config.map((item: DrawerContentConfig) => (
        <AccountAccordionItem key={item.text} item={item} pathname={pathname} navigateTo={navigate} />
      ))}
    </List>
  );
};

type AccordionItemProps = {
  item: DrawerContentConfig;
  pathname: string;
  navigateTo: (path: string) => void;
};

const AccountAccordionItem = ({ item, pathname, navigateTo }: AccordionItemProps) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const autoExpanded = item.children.some((child) => {
      const pattern = `${child.path}/*`;
      return pathname === child.path || !!matchPath({ path: pattern }, pathname);
    });
    setExpanded(autoExpanded);
  }, [pathname, item]);

  return (
    <Accordion
      sx={item.path === pathname ? styles.accordionActive : styles.accordion}
      expanded={expanded}
      elevation={0}
      onChange={(_, value) => item.children.length && setExpanded(value)}
      disableGutters
      onClick={() => item.path && navigateTo(item.path)}
    >
      <AccordionSummary sx={styles.accordionSummary} expandIcon={<ArrowRightIcon sx={styles.expandIcon} />}>
        {item.icon}
        <Typography
          variant='body2'
          sx={{
            color: 'neutral.100',
            pl: 1,
          }}
        >
          {item.text}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {item.children.map((link, index, arr) => (
          <Fragment key={link.path}>
            <ListItem sx={styles.childItem}>
              <ListItemButton
                disableRipple
                sx={styles.childButton}
                onClick={() => navigateTo(link.path)}
                disableGutters
              >
                <Typography
                  variant='body2'
                  color={link.path === pathname ? 'neutral.100' : 'neutral.200'}
                  sx={{
                    fontWeight: link.path === pathname ? 600 : 'initial',
                    pl: 2,
                  }}
                >
                  {link.text}
                </Typography>
              </ListItemButton>
            </ListItem>
            {index !== arr.length - 1 && <Divider variant='middle' component='li' sx={styles.childDivider} />}
          </Fragment>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default GolaAccountDrawerContent;
